import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Table, Checkbox, Space, Typography, Button, Popconfirm } from "antd";
import { SyncOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";

import css from "./ProductsListComponent.module.css";
import { productsActions } from "../../../store/slices/settings-slices";
import { ModalForDelete } from "../../../ui";
import { ProductsUpdateProductModal } from "../settingSectionModals/ProductsUpdateProductModal/ProductsUpdateProductModal";
import { productsService } from "../../../services/settings-services";
import { dishesService } from "../../../services/dishes-services";

const { Text } = Typography;

const ProductsListComponent = ({ groupingMode = "grouped" }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  let { productsArray, trigger, productsFiltered, queryParams, isLoading } = useSelector(
    (store) => store.products
  );
  const [isOpenUpdate, setIsOpenUpdate] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});
  const [pageSize, setPageSize] = useState(15);

  useEffect(() => {
    dispatch(productsActions.getAllProducts());
  }, [dispatch, trigger]);

  if (productsFiltered?.length > 0) {
    productsArray = productsFiltered;
  }

  // If a specific group is selected via query params, narrow the list
  const selectedGroupId = queryParams?.groupId;
  if (selectedGroupId) {
    productsArray = productsArray?.filter((p) => p.groupId === selectedGroupId);
  }

  const currentLanguage = i18n.language;

  const handleExcludableChange = async (productId, checked) => {
    try {
      await productsService.updateProductById(productId, {
        excludable: checked,
      });
      dispatch(productsActions.changeTrigger());
      toast.success("Product updated successfully!");
    } catch (error) {
      console.error("Error updating product:", error);
      toast.error("Error updating product");
    }
  };

  const handleUpdateClick = (product) => {
    setSelectedProduct(product);
    setIsOpenUpdate(true);
  };

  const handleDeleteClick = async (product) => {
    try {
      setLoadingStates((prev) => ({ ...prev, [product.id]: true }));
      await productsService.deleteProductById(product.id);
      dispatch(productsActions.changeTrigger());
      toast.success("Product is deleting!");
      setIsOpenDeleteModal(false);
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error("Error deleting product");
    } finally {
      setLoadingStates((prev) => ({ ...prev, [product.id]: false }));
    }
  };

  const handleSyncClick = async (productId) => {
    try {
      setLoadingStates((prev) => ({ ...prev, [productId]: true }));
      await dishesService.syncWithProductsAndSemiFinishedProducts(productId);
      dispatch(productsActions.changeTrigger());
      toast.success("Dishes synced successfully!");
    } catch (error) {
      console.error("Error syncing dishes:", error);
      toast.error("Error syncing dishes");
    } finally {
      setLoadingStates((prev) => ({ ...prev, [productId]: false }));
    }
  };

  const columns = [
    {
      title: t("modal-name-input-new-category"),
      dataIndex: "name",
      key: "name",
      width: 180,
      render: (name) => name[currentLanguage] || name["en"],
    },
    {
      title: t("Brand"),
      dataIndex: "brand",
      key: "brand",
      width: 150,
      render: (brand) => {
        if (!brand || !brand.name) return "-";
        return brand.name[currentLanguage] || brand.name["en"] || brand.name;
      },
    },
    {
      title: t("Excludable"),
      dataIndex: "excludable",
      key: "excludable",
      width: 110,
      align: "center",
      render: (excludable, record) => (
        <Checkbox
          checked={excludable}
          onChange={(e) => handleExcludableChange(record.id, e.target.checked)}
        />
      ),
    },
    {
      title: t("modal-Description-input"),
      dataIndex: "description",
      key: "description",
      width: 250,
      render: (description) => (
        <Text
          ellipsis={{
            tooltip: description[currentLanguage] || description["en"],
          }}
        >
          {description[currentLanguage] || description["en"]}
        </Text>
      ),
    },
    {
      title: t("modal-energy"),
      dataIndex: "energy",
      key: "energy",
      width: 100,
      align: "right",
    },
    {
      title: t("modal-proteins"),
      dataIndex: "proteins",
      key: "proteins",
      width: 100,
      align: "right",
    },
    {
      title: t("modal-fats"),
      dataIndex: "fats",
      key: "fats",
      width: 100,
      align: "right",
    },
    {
      title: t("Carbohydrates"),
      dataIndex: "carbohydrates",
      key: "carbohydrates",
      width: 100,
      align: "right",
    },
    {
      title: t("modal-price"),
      dataIndex: "price",
      key: "price",
      width: 100,
      align: "right",
    },
    {
      title: t("Actions"),
      key: "actions",
      fixed: "right",
      width: 350,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<SyncOutlined />}
            onClick={() => handleSyncClick(record.id)}
            loading={loadingStates[record.id]}
            disabled={loadingStates[record.id]}
          >
            Sync
          </Button>
          <Button
            type="default"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleUpdateClick(record)}
            loading={loadingStates[record.id]}
            disabled={loadingStates[record.id]}
          >
            Update
          </Button>
          {/* <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              setSelectedProduct(record);
              setIsOpenDeleteModal(true);
            }}
          >
            Delete
          </Button> */}
          <Popconfirm
            title={t("Are you sure you want to delete this product?")}
            onConfirm={() => handleDeleteClick(record)}
            okText={t("Yes")}
            cancelText={t("No")}
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
              loading={loadingStates[record.id]}
              disabled={loadingStates[record.id]}
            >
              {t("Delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const groupedProducts = (() => {
    const groups = {};
    (productsArray || []).forEach((p) => {
      const nameObj = p.group?.name || {};
      const displayName =
        nameObj[currentLanguage] || nameObj["en"] || "No group";
      if (!groups[displayName]) groups[displayName] = [];
      groups[displayName].push(p);
    });
    return groups;
  })();

  const renderGroupedTable = () => {
    return Object.entries(groupedProducts).map(([groupName, list]) => (
      <div key={groupName} style={{ marginBottom: 24 }}>
        <div
          className={css.groupHeader}
          style={{
            fontSize: "16px",
            fontWeight: "600",
            padding: "12px 16px",
            background: "#f5f5f5",
            borderRadius: "4px",
            marginBottom: "8px",
          }}
        >
          {groupName}
        </div>
        <Table
          columns={columns}
          dataSource={list}
          rowKey="id"
          pagination={{
            pageSize: pageSize,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
            onChange: (page, pageSize) => setPageSize(pageSize),
          }}
          scroll={{ x: 1480 }}
          size="small"
          loading={isLoading}
        />
      </div>
    ));
  };

  return (
    <section className={css.wrap}>
      {groupingMode === "grouped" ? (
        renderGroupedTable()
      ) : (
        <Table
          columns={columns}
          dataSource={productsArray || []}
          rowKey="id"
          pagination={{
            pageSize: pageSize,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
            onChange: (page, pageSize) => setPageSize(pageSize),
          }}
          scroll={{ x: 1480 }}
          size="small"
          loading={isLoading}
        />
      )}

      {isOpenUpdate && selectedProduct && (
        <ProductsUpdateProductModal
          setIsOpenUpdate={setIsOpenUpdate}
          product={selectedProduct}
          setLoadingStates={setLoadingStates}
          loadingStates={loadingStates}
        />
      )}

      {isOpenDeleteModal && selectedProduct && (
        <ModalForDelete
          selectedProduct={selectedProduct}
          handleDeleteClick={handleDeleteClick}
          name={"product"}
        />
      )}
    </section>
  );
};

export default React.memo(ProductsListComponent);
